import { STATION_ITEM_TYPE, STATION_TYPE } from "@/enums/station-type";
import { Station, StationItem } from "../interface/archi/station";
import { LineT, Point } from "pytha";
import { Vector2, Vector3 } from "three"
import { useEffect, useRef } from "react";

// 生成站台的名称 例：4A+3B*242
export function generateStationName(station: Station) {
    let stationItemList = station?.itemList || []
    let aList = stationItemList.filter(item => item.type == STATION_ITEM_TYPE.STATION)
    let bList = stationItemList.filter(item => item.type == STATION_ITEM_TYPE.TRACK)
    let aStr = aList.length.toString() + "A"
    let bStr = bList.length.toString() + "B*"
    bList.sort((a, b) => a.level - b.level)
    for (let i = 0; i < bList.length; i++) {
        bStr += bList[i].count.toString()
    }
    return aStr + "+" + bStr
}

// 生成站台的图元, 在xoz平面绘制
export function generateStationPrimitive(station: Station) {
    let lineList: LineT[] = []
    // 定义画笔位置
    let penPosition: Point = new Point(0, 0, 0)
    // 排序站台的item，排序成真实序列
    let realList: StationItem[] = []
    let stationItemList = station?.itemList || []
    let aList = stationItemList.filter(item => item.type == STATION_ITEM_TYPE.STATION)
    let bList = stationItemList.filter(item => item.type == STATION_ITEM_TYPE.TRACK)
    aList.sort((a, b) => a.level - b.level)
    bList.sort((a, b) => a.level - b.level)
    let maxLength = Math.max(aList.length, bList.length)
    for (let i = 0; i < maxLength; i++) {
        // 判断站台在前 | 股道在前 -> 判断添加顺序
        if (station.type == STATION_TYPE.STATION_FIRST) {
            if (i < aList.length) {
                realList.push(aList[i])
            }
            if (i < bList.length) {
                realList.push(bList[i])
            }
        } else {
            if (i < bList.length) {
                realList.push(bList[i])
            }
            if (i < aList.length) {
                realList.push(aList[i])
            }
        }
    }
    // 遍历station子图元绘制line
    let height: number = station.height * 1000 || 0;
    for (let i = 0; i < realList.length; i++) {
        if (realList[i].type == STATION_ITEM_TYPE.STATION) {
            // 若是站台，画笔向z正方向移动，同时绘制出线
            let stationLine1: LineT = new LineT()
            stationLine1._geo.start.copy(penPosition)
            penPosition.move(new Vector3(0, 0, 1), height)
            stationLine1._geo.end.copy(penPosition)
            // 绘制站台横线
            let stationLine2: LineT = new LineT()
            stationLine2._geo.start.copy(penPosition)
            penPosition.move(new Vector3(1, 0, 0), realList[i].width * 1000)
            stationLine2._geo.end.copy(penPosition)
            // 画笔向z负方向移动，同时绘制出线
            let stationLine3: LineT = new LineT()
            stationLine3._geo.start.copy(penPosition)
            penPosition.move(new Vector3(0, 0, -1), height)
            stationLine3._geo.end.copy(penPosition)
            // 添加进线集合
            lineList.push(stationLine1)
            lineList.push(stationLine2)
            lineList.push(stationLine3)
        } else {
            // 若是股道 绘制站台横线
            let trackLine1: LineT = new LineT()
            trackLine1._geo.start.copy(penPosition)
            penPosition.move(new Vector3(1, 0, 0), realList[i].width * 1000)
            trackLine1._geo.end.copy(penPosition)
            // 添加进线集合
            lineList.push(trackLine1)
        }
    }
    return lineList
}

interface TrackPosition {
    firstPoint: Vector2
    endPoint: Vector2
}
// 站台示意图canvas获取方法
export function StationCanvas(props: { station: Station }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            draw(ctx);
        }
    }, []);

    const draw = (ctx: CanvasRenderingContext2D) => {
        // 排序站台的item，排序成真实序列
        let realList: StationItem[] = []
        let stationItemList = props.station?.itemList || []
        let aList = stationItemList.filter(item => item.type == STATION_ITEM_TYPE.STATION)
        let bList = stationItemList.filter(item => item.type == STATION_ITEM_TYPE.TRACK)
        aList.sort((a, b) => a.level - b.level)
        bList.sort((a, b) => a.level - b.level)
        let maxLength = Math.max(aList.length, bList.length)
        let canvasWidth = 0
        let height = 22
        let width = 30
        for (let i = 0; i < maxLength; i++) {
            // 判断站台在前 | 股道在前 -> 判断添加顺序
            if (props.station.type == STATION_TYPE.STATION_FIRST) {
                if (i < aList.length) {
                    canvasWidth += width
                    realList.push(aList[i])
                }
                if (i < bList.length) {
                    canvasWidth += bList[i].count * width
                    realList.push(bList[i])
                }
            } else {
                if (i < bList.length) {
                    canvasWidth += bList[i].count * width
                    realList.push(bList[i])
                }
                if (i < aList.length) {
                    canvasWidth += width
                    realList.push(aList[i])
                }
            }
        }
        // 保存站台名称位置与股道名称位置
        let stationNamePositions: Vector2[] = []
        let trackNamePositions: TrackPosition[] = []
        // 设置画布大小
        ctx.canvas.width = canvasWidth + 18;
        ctx.canvas.height = 60;

        // 设置画笔位置
        let penPosition: Vector2 = new Vector2(16, 38)
        // 添加H文字
        ctx.font = '10px'
        ctx.fillStyle = 'black'
        ctx.fillText("H", 4, 32)
        // 遍历站台item，开始绘制
        for (let i = 0; i < realList.length; i++) {
            if (realList[i].type == STATION_ITEM_TYPE.STATION) {
                // 若是站台，画笔向y负方向移动，同时绘制出线
                ctx.beginPath();
                ctx.moveTo(penPosition.x, penPosition.y)
                penPosition.y -= height
                ctx.lineTo(penPosition.x, penPosition.y)
                // 添加站台名称坐标
                stationNamePositions.push(penPosition.clone())
                // 绘制站台横线
                penPosition.x += width
                ctx.lineTo(penPosition.x, penPosition.y)
                // 向负方向移动，绘制另一条线
                penPosition.y += height
                ctx.lineTo(penPosition.x, penPosition.y)
                ctx.stroke()
            } else {
                // 循环绘制站台横线
                let firstPoint = penPosition.clone()
                for (let j = 0; j < realList[i].count; j++) {
                    ctx.beginPath()
                    ctx.moveTo(penPosition.x, penPosition.y)
                    penPosition.x += width
                    ctx.lineTo(penPosition.x, penPosition.y)
                    if (j != realList[i].count - 1) {
                        ctx.lineTo(penPosition.x, penPosition.y - 6)
                    }
                    ctx.stroke()
                }
                let endPoint = penPosition.clone()
                // 添加进名称集合
                trackNamePositions.push({ firstPoint: firstPoint, endPoint: endPoint })
            }
        }

        // 绘制站台名称
        for (let i = 0; i < stationNamePositions.length; i++) {
            let name = 'A' + (i + 1).toString()
            ctx.font = '9px'
            ctx.strokeStyle = "#363636"
            ctx.fillStyle = 'black'
            ctx.fillText(name, stationNamePositions[i].x + 9, stationNamePositions[i].y - 6)
        }

        let trackPenPosition: Vector2 = new Vector2()
        // 绘制股道名称与线
        for (let i = 0; i < trackNamePositions.length; i++) {
            // 绘制线
            trackPenPosition.copy(trackNamePositions[i].firstPoint)
            trackPenPosition.y += 5
            ctx.beginPath()
            ctx.strokeStyle = "#999"
            ctx.moveTo(trackPenPosition.x, trackPenPosition.y)
            trackPenPosition.y += 3
            ctx.lineTo(trackPenPosition.x, trackPenPosition.y)
            trackPenPosition.copy(trackNamePositions[i].endPoint)
            trackPenPosition.y += 8
            ctx.lineTo(trackPenPosition.x, trackPenPosition.y)
            trackPenPosition.y -= 3
            ctx.lineTo(trackPenPosition.x, trackPenPosition.y)
            ctx.stroke()
            // 绘制名字
            let name = 'B' + (i + 1).toString()
            ctx.font = '9px'
            ctx.strokeStyle = "#363636"
            ctx.fillStyle = 'black'
            ctx.fillText(
                name,
                (trackNamePositions[i].firstPoint.x + trackNamePositions[i].endPoint.x) / 2 - 5,
                trackNamePositions[i].firstPoint.y + 20
            )
        }
    };

    return (
        <canvas
            style={{
                height: "60px",
                margin: "auto"
            }}
            ref={canvasRef}
        />
    )
};